<template>
  <dialog-scroll-lock
    v-if="(source.card || source.id) && tabKey"
    v-bind="$attrs"
    v-on="$listeners"
    width="800" scrollable
    :value="true"
    :content-class="`white fill-height overflow-x-hidden ${selected ? 'no-touch-action' : ''}`"
    @input="$router.push({ hash: undefined })"
  >

    <error-dialog :status="sendSampleStatus" />

    <template v-if="showGiftcardBlob">
      <giftcard-add-dialog
        v-if="showGiftcardBlob.user_id == card.user_id"
        :blob="showGiftcardBlob"
        @input="showGiftcardBlob = null"
      />
      <giftcard-edit-dialog
        v-else
        :order="card"
        :blob="showGiftcardBlob"
        :max-giftcard-value="5000"
        :max-giftcard-value-remaining="5000"
        @input="showGiftcardBlob = null"
      />
    </template>

    <dialog-scroll-lock v-if="showEvent" width="600" v-model="showEvent">
      <v-card>
        <div>
          <order-stepper-event
            class="relative mt-n2"
            style="z-index: 1;"
            :hide-preview="true"
            :order-id="null"
            :order="card"
            :contributors="{ isSent: false, nOther: 0, deliveriesByTarget: {}, uninvited: [] }"
            :deliveries="{ byTarget: {} }"
            :responses="{ list: [], byKey: {} }"
          />
        </div>
        <v-divider />
        <v-card-actions class="justify-end">
          <v-btn @click="showEvent = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock v-if="showSend" width="300" v-model="showSend">

      <!-- Note that this must be inside of the 'showSend' dialog for the z-index to work correctly -->
      <captcha-overlay v-if="showCaptcha" @cancel="showCaptcha = false" @complete="sendSample" />

      <v-card>
        <v-card-title>Send yourself a sample</v-card-title>
        <v-card-text>
          <v-form v-model="isEmailValid" @submit.prevent="showCaptcha = true">
            <v-text-field
              v-model="email" name="email-address" label="Your email address" persistent-placeholder hide-details
              :rules="[ v => !!(v && EMAIL_REGEX.test(v)) ]"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text :disabled="sendSampleStatus.pending" @click="showSend = false">Cancel</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!isEmailValid || !isFormValid"
            :loading="sendSampleStatus.pending"
            @click="showCaptcha = true"
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <div class="relative d-flex flex-column" v-bind:style="{ 'background-color': card?.skin.page_background_color }">

      <card-background v-if="card" :src="card.skin.page_background_img" />

      <confetti
        v-if="card && card.skin.open_effect"
        :key="card.skin.open_effect"
        class="absolute-fill overflow-hidden"
        style="z-index: 2;"
        :effect="card.skin.open_effect"
        :active="true"
      />

      <div class="relative" style="z-index: 3;">
        <div class="pa-4 d-flex" style="backdrop-filter: blur(16px); -webpack-backdrop-filter: blur(16px); background-color: #0002;">
          <div class="flex-grow-1 header-font text-h4 white--text font-weight-bold" style="text-shadow: 1px 1px 3px #000A;">
            <span class="hidden-sm-and-up">Demo</span>
            <span class="hidden-xs-only">Try our editing tools</span>
          </div>
          <v-btn v-if="global.profile?.admin && source.id" class="mr-3" exact small fab :to="{ name: 'admin-order', params: { token: source.id } }">
            <v-icon v-html="mdiOpenInNew" />
          </v-btn>
          <v-fade-transition>
            <div v-if="tabI" class="relative mr-3">
              <v-btn
                class="px-2 px-md-3 elevation-6"
                style="height: 40px; right: 100%; min-width: unset;"
                absolute exact rounded
                :to="getRoute(tabs[tabI - 1].key)"
              >
                <v-icon class="ml-md-n1" v-html="mdiChevronLeft" />
                <span class="hidden-sm-and-down">Back</span>
              </v-btn>
            </div>
          </v-fade-transition>
          <v-btn
            class="mr-3 elevation-6 pulse"
            style="height: 40px;"
            color="primary" exact rounded
            v-bind="tabI == tabs.length - 1 ? {} : { to: getRoute(tabs[tabI + 1].key) }"
            @click.capture.stop.prevent="() => {
              if (tabI == tabs.length - 1)
                showSend = true;
              else
                $router.push(getRoute(tabs[tabI + 1].key));
            }"
          >
            Next
            <v-icon class="mr-n1" v-html="mdiChevronRight" />
          </v-btn>
          <v-btn exact small fab :to="{ hash: undefined }">
            <v-icon v-html="mdiClose" />
          </v-btn>
        </div>
      </div>
      <v-progress-linear v-if="!card" indeterminate />
      <div v-else class="flex-grow-1 relative" style="z-index: 3;">
        <div class="absolute-fill" style="z-index: 1; transition: opacity .12s;" v-bind:style="{ opacity: (tabs[tabI].key == 'sign') ? 1 : 0 }">
          <card-editor
            ref="sample-editor"
            mode="edit"
            is-owner
            show-event-details
            :card="card"
            @loaded-cover="onLoadedCover"
            @select-blob="x => selected = x"
            @click-giftcard="x => showGiftcardBlob = x"
            @insert-blob="(_, x) => x.sample = true"

            @step-to="v => showEvent = (v == 'event')"
            @click-event-details="showEvent = true"
          />
        </div>
        <v-slide-x-transition>
          <div v-if="tabs[tabI].key == 'theme'" class="fill-height relative" style="z-index: 2;">
            <order-stepper-theme
              class="fill-height"
              style="min-height: 446px; z-index: 2;"
              :order="card"
              :org-skin="NO_LOGO_SKIN"
              :hide-background-confetti="true"
              @input="v => Object.assign(card, v)"
            />
          </div>
        </v-slide-x-transition>
      </div>
    </div>
  </dialog-scroll-lock>
</template>

<style scoped>
@media (min-width: 600px) {
  .absolute-fill-sm {
    position: absolute;
    inset: 0;
  }
}
</style>

<style>
.no-touch-action {
  touch-action: none;
}
</style>

<script>
import CaptchaOverlay from '@/components/CaptchaOverlay.vue';
import CardBackground from '@/components/CardBackground.vue';
import CardEditor from '@/components/CardEditor.vue';
import Confetti from '@/components/Confetti.vue';
import Envelope from '@/components/Envelope.vue';
import GiftcardAddDialog from '@/components/GiftcardAddDialog.vue';
import GiftcardEditDialog from '@/components/GiftcardEditDialog.vue';
import OrderStepperEvent from '@/components/OrderStepperEvent.vue';
import OrderStepperTheme from '@/components/OrderStepperTheme.vue';
import DialogScrollLockLoader from '@/components/DialogScrollLockLoader.vue';
import getBlobMediaFiles from '@/utils/card-media.js';
import { EMAIL_REGEX } from '@/utils/email.js';
import { updateEventLinks } from '@/utils/event-links.js';
import { LOCALE, TZ } from '@/utils/intl.js';
import modernize from '@/utils/order-modernize.js';
import { DEFAULT } from '@/utils/skin.js';
import { getTimezoneName, getDateStr, getTimeRangeStr, getISO, getUTCOffset } from '@/utils/time.js';
import uuid4 from '@/utils/uuid.js';
import iconPathEnvPalette from 'raw-loader!@/assets/icon-path-env-palette.txt';
import { mdiClose, mdiEye, mdiPencil, mdiCalendar, mdiChevronLeft, mdiChevronRight, mdiMenuDown, mdiSend, mdiAlert, mdiOpenInNew } from '@mdi/js';


export default {
  name: 'send-sample-dialog',

  components: {
    CaptchaOverlay,
    CardBackground,
    CardEditor,
    Confetti,
    Envelope,
    GiftcardAddDialog,
    GiftcardEditDialog,
    OrderStepperEvent,
    OrderStepperTheme,
    DialogScrollLockLoader
  },

  props: {
    hash: { type: String, default: '#demo' },
    sample: Object, // Load the sample directly (if the 'hash' is active)
    sampleId: String, // Asynchronously load the sample ID (if the 'hash' is active)
    samples: Array, // Each entry has a 'hash' property and an 'id' property (the 'hash' property is ignored)
  },

  data() {
    return {
      email: null,
      isEmailValid: false,
      isFormValid: true,
      card: null,
      selected: null,
      showSend: false,
      showCaptcha: false,
      showGiftcardBlob: null,
      showEvent: false,
      sendSampleStatus: this.$track('sendSample')
    }
  },

  computed: {
    form() {
      if (!this.card)
        return null;
      for (const page of this.card.pages)
        for (const blob of page.blobs)
          if ((blob.type == 'BTN') && blob.btn.form)
            return blob.btn.form;
      return null;
    },

    tabs() {
      return [
        { key: 'sign', name: 'Design', icon: mdiPencil },
        { key: 'theme', name: 'Envelope / theme', icon: iconPathEnvPalette }
      ]
    },

    tabKey() {
      return this.$route.hash.split(':')[1];
    },

    tabI() {
      // The tab index of the active tab
      return this.tabs.findIndex(x => x.key == this.tabKey);
    },

    source() {
      if (this.sample)
        return this.$route.hash.startsWith(this.hash) ? { card: this.sample } : {};
      if (this.sampleId)
        return this.$route.hash.startsWith(this.hash) ? { id: this.sampleId } : {};
      return { id: this.samples.find(x => x.hash == this.$route.hash.split(':')[0])?.id };
    }
  },

  created() {
    Object.assign(this, { EMAIL_REGEX, NO_LOGO_SKIN: _.pick(DEFAULT, [ 'header_logo', 'email_footer_logo', 'email_footer_url' ]), mdiClose, mdiEye, mdiPencil, mdiChevronLeft, mdiChevronRight, mdiMenuDown, mdiSend, mdiAlert, mdiOpenInNew });
  },

  methods: {
    getRoute(key) {
      return { hash: this.$route.hash.split(':')[0] + ':' + key };
    },

    onLoadedCover() {
      // Called when the cover in the sample dialog is loaded
      setTimeout(() => this.$refs['sample-editor'] && this.$refs['sample-editor'].swipe(1), 500);
    },

    async sendSample(captcha) {
      this.showCaptcha = false;
      if (!this.email)
        return;
      const files = await getBlobMediaFiles(this.card);
      const params = {
        ...this.card,
        email: this.email,
        files,
        captcha
      };
      for (const page of params.pages)
        for (const blob of page.blobs)
          if (blob.user_id == '-')
            blob.user_id = '00000000-0000-0000-0000-000000000000';
      await this.$call('send-sample', params);
      await this.$confirm({
        title: 'Email sent',
        msg: `Check your email to see what it looks like to receive ${this.form ? 'an invitation' : 'a card'} from Ellacard. Sign in to your <a href="/account/orders">account</a> to see how you can manage an order${this.form ? ' and track RSVPs' : ''}.`,
        buttons: [ 'Ok' ],
        primary: 'Ok'
      });
      this.$router.push({ hash: undefined });
    },

    async getOrder(order_id) {
      return await this.$call('get-order', { order_id });
    },

    prepareSample(sample) {
      const order =  _.cloneDeep(sample);
      modernize(order);
      const event = order.event;
      if (event) {
        // Make sure we convert the event details to the user's timezone as opposed to the one in which this order was
        // set up
        const offset = getUTCOffset(TZ, event.from);
        event.tz = TZ;
        if (event.date_start && event.time_start)
          event.from = (new Date(event.date_start + 'T' + event.time_start + offset)).toISOString();
        if (event.date_start && event.time_end)
          event.to = (new Date(event.date_start + 'T' + event.time_end + offset)).toISOString();
        if (event.from && event.to && (event.to <= event.from)) {
          const to = new Date(event.to);
          to.setDate(to.getDate() + 1);
          event.to = to.toISOString();
        }
        event.from = event.from || null;
        event.to = event.to || null;
        event.lz_date = getDateStr(new Date(event.from));
        event.lz_time_range = getTimeRangeStr(new Date(event.from), new Date(event.to));
        event.lz_tz_name = getTimezoneName(new Date(event.from), 'short');
        updateEventLinks(order, event);
      }
      for (const page of order.pages) {
        for (const blob of page.blobs) {
          if (blob.for_design_id)
            blob.user_id = null;
          else if (blob.user_id && (order.type == 'GROUP'))
            blob.user_id = '0009324d-2d38-4b95-89b3-f1786f72d8c7'; // demo@ellacard.com
          else if (blob.user_id)
            blob.user_id = this.globalUserId || '-';
          if (blob.type == 'GIFTCARD') {
            blob.user_id = this.globalUserId || '-';
            blob.sample = true;
          }
        }
      }
      order.state = 'PENDING';
      order.lock = null;
      order.locale = LOCALE;
      order.tz = TZ;
      order.info.from = 'Ellacard';
      order.info.to = 'You';
      order.info.contributor_groups = [];
      order.info.recipient_groups = [ { to: 'You', targets: [ '' ] } ];
      if (order.type != 'INVITATION')
        order.multiple_recipients = false;
      return order;
    }
  },

  watch: {
    source: {
      immediate: true,
      async handler(v, prev) {
        if (_.isEqual(v, prev))
          return;
        this.card = null;
        if (v.card)
          this.card = this.prepareSample(v.card);
        else if (v.id)
          this.card = this.prepareSample(await this.getOrder(v.id));

        if ((this.source.card || this.source.id) && !this.tabKey)
          this.$router.replace({ hash: this.$route.hash + ':sign' });
      }
    },

    isSignedIn: {
      immediate: true,
      handler(v) {
        this.email = v ? this.global.profile.email : null;
      }
    },

    showSend(v) {
      // To cover a weird case where pressing the esc key with 'showCaptcha' set to true does not clean up correctly
      if (!v)
        this.showCaptcha = false;
    },

    'card.multiple_recipients'(v) {
      if (v && (this.card.type != 'INVITATION')) {
        this.$confirm({ msg: 'Sample orders cannot be sent to multiple recipients', buttons: [ 'Ok' ], primary: 'Ok' });
        this.card.multiple_recipients = false;
      }
    }
  }
}
</script>
